// Hospital Service
// Handles all hospital-related API calls
import { api, v1Api, handleApiError, handleApiSuccess } from './api.js';
import apiService from './apiService.js';
import authService from './authService.js';
import { API_CONFIG, getV1EndpointUrl } from '../config/api.js';

class HospitalService {
  // ============================================================================
  // HOSPITAL PROFILE MANAGEMENT
  // ============================================================================

  async getMyProfile() {
    // GET /v1/hospital/my-profile - fetch full hospital profile for edit form
    try {
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.MY_PROFILE;
      const response = await v1Api.get(endpoint);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async updateMyProfile(profileData) {
    // PUT /v1/hospital/my-profile - request body must match API exactly
    try {
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.MY_PROFILE;
      const body = {
        name: profileData.name ?? '',
        phone: profileData.phone ?? '',
        phone_country_code: profileData.phone_country_code ?? '+91',
        address: profileData.address ?? '',
        city: profileData.city ?? '',
        state: profileData.state ?? '',
        pincode: profileData.pincode ?? '',
        contactPerson: profileData.contactPerson ?? '',
        contactPersonPhone: profileData.contactPersonPhone ?? '',
        contactPersonPhone_country_code: profileData.contactPersonPhone_country_code ?? '+91',
        contactPersonEmail: profileData.contactPersonEmail ?? ''
      };
      const response = await v1Api.put(endpoint, body);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getProfileById(hospitalId) {
    return await apiService.getHospitalById(hospitalId);
  }

  async updateProfileById(hospitalId, profileData) {
    return await apiService.updateHospital(hospitalId, profileData);
  }

  // ============================================================================
  // HOSPITAL DASHBOARD
  // ============================================================================

  async getMyDashboard(params = {}) {
    return await apiService.getMyHospitalDashboard(params);
  }

  async getDashboardById(hospitalId, params = {}) {
    return await apiService.getHospitalById(hospitalId);
  }

  // ============================================================================
  // HOSPITAL ANALYTICS
  // ============================================================================

  async getAnalytics(params = {}) {
    return await apiService.getHospitalAnalytics(params);
  }

  async getPerformanceComparison(params = {}) {
    try {
      const response = await apiService.get('/hospital/analytics/performance-comparison', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // HOSPITAL PAYMENTS & SETTLEMENTS
  // ============================================================================

  async getSettlements(params = {}) {
    return await apiService.getHospitalSettlements(params);
  }

  async processPayment(paymentData) {
    return await apiService.processHospitalPayment(paymentData);
  }

  async getPaymentSummary(params = {}) {
    try {
      const response = await apiService.get('/hospital/payment/summary', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createPaymentOrder(orderData) {
    try {
      // Endpoint: POST /v1/hospital/payments/create-order
      const endpoint = '/v1/hospital/payments/create-order';
      const response = await v1Api.post(endpoint, orderData);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getAllPayments(params = {}) {
    try {
      // Endpoint: GET /v1/hospital/payments/all
      const endpoint = '/v1/hospital/payments/all';
      const response = await v1Api.get(endpoint, { params });
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // ============================================================================
  // HOSPITAL GROUPS
  // ============================================================================

  async createGroup(groupData) {
    return await apiService.createHospitalGroup(groupData);
  }

  async getAllGroups(params = {}) {
    return await apiService.getAllHospitalGroups(params);
  }

  async getGroupById(groupId) {
    return await apiService.getHospitalGroup(groupId);
  }

  async updateGroup(groupId, groupData) {
    try {
      const response = await apiService.put(`/api/hospital/groups/${groupId}`, groupData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteGroup(groupId) {
    try {
      const response = await apiService.delete(`/api/hospital/groups/${groupId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async addGroupMember(groupId, memberData) {
    return await apiService.addGroupMember(groupId, memberData);
  }

  async removeGroupMember(groupId, memberId) {
    return await apiService.removeGroupMember(groupId, memberId);
  }

  async getGroupAnalytics(groupId, params = {}) {
    try {
      const response = await apiService.get(`/api/hospital/groups/${groupId}/analytics`, params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // HOSPITAL NOTIFICATIONS
  // ============================================================================

  async getNotifications(params = {}) {
    return await apiService.getHospitalNotifications(params);
  }

  async markNotificationAsRead(notificationId) {
    return await apiService.markNotificationAsRead(notificationId);
  }

  async markAllNotificationsAsRead() {
    return await apiService.markAllNotificationsAsRead();
  }

  async sendEmergencyNotification(notificationData) {
    return await apiService.sendEmergencyNotification(notificationData);
  }

  async getNotificationStats(params = {}) {
    try {
      const response = await apiService.get('/hospital/notifications/stats', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // HOSPITAL BOOKINGS
  // ============================================================================

  async createBooking(bookingData) {
    try {
      // PRIORITY 1: Always fetch from localStorage first (most reliable source)
      // This ensures hospital_id is always present even if not passed in bookingData
      if (typeof window !== 'undefined') {
        try {
          // First, try to get hospital_id directly from dedicated storage key (fastest)
          const hospitalIdKey = API_CONFIG.STORAGE_KEYS.HOSPITAL_ID;
          const hospitalIdFromKey = localStorage.getItem(hospitalIdKey);

          if (hospitalIdFromKey) {
            bookingData.hospital_id = String(hospitalIdFromKey);
          } else {
            // PRIORITY 2: Check hospital_data storage
            const hospitalDataKey = API_CONFIG.STORAGE_KEYS.HOSPITAL_DATA;
            const hospitalDataStr = localStorage.getItem(hospitalDataKey);
            if (hospitalDataStr) {
              try {
                const hospitalData = JSON.parse(hospitalDataStr);
                const hospitalIdFromData = hospitalData.hospital_id || hospitalData.id;
                if (hospitalIdFromData) {
                  bookingData.hospital_id = String(hospitalIdFromData);
                  // Also store it in the dedicated key for future use
                  localStorage.setItem(hospitalIdKey, String(hospitalIdFromData));
                }
              } catch (e) {
                // Failed to parse hospital_data
              }
            }

            // PRIORITY 3: Try user data
            if (!bookingData.hospital_id) {
              const userDataKey = API_CONFIG.STORAGE_KEYS.USER_DATA;
              const userData = localStorage.getItem(userDataKey);

              if (userData) {
                try {
                  const parsedUser = JSON.parse(userData);

                  // Check multiple possible field names for hospital_id
                  const hospitalIdFromStorage =
                    parsedUser.hospital_id ||
                    parsedUser.hospitalId ||
                    parsedUser.hospital?.hospital_id ||
                    parsedUser.hospital?.id ||
                    parsedUser.operator_id; // Sometimes operator_id might be the hospital_id

                  if (hospitalIdFromStorage) {
                    bookingData.hospital_id = String(hospitalIdFromStorage);
                    // Also store it in the dedicated key for future use
                    localStorage.setItem(hospitalIdKey, String(hospitalIdFromStorage));
                  }
                } catch (e) {
                  // Failed to parse user data
                }
              } else {
                // Try alternative keys
                const alternativeKeys = ['user', 'userData', 'auth_user', 'currentUser'];
                for (const key of alternativeKeys) {
                  const altData = localStorage.getItem(key);
                  if (altData) {
                    try {
                      const parsed = JSON.parse(altData);
                      const altHospitalId = parsed.hospital_id || parsed.hospitalId || parsed.hospital?.hospital_id;
                      if (altHospitalId) {
                        bookingData.hospital_id = String(altHospitalId);
                        // Also store it in the dedicated key for future use
                        localStorage.setItem(hospitalIdKey, String(altHospitalId));
                        break;
                      }
                    } catch (e) {
                      // Failed to parse data from key
                    }
                  }
                }
              }
            }
          }
        } catch (error) {
          // Error getting hospital_id from localStorage
        }
      }

      // PRIORITY 2: Convert hospitalId to hospital_id if needed (API expects snake_case)
      if (bookingData.hospitalId && !bookingData.hospital_id) {
        bookingData.hospital_id = String(bookingData.hospitalId);
        delete bookingData.hospitalId; // Remove camelCase version
      }

      // Validate that hospital_id is present and valid
      if (!bookingData.hospital_id) {
        throw new Error('Hospital ID is required for booking. Please ensure you are logged in as a hospital user and try refreshing the page. If the issue persists, please contact support.');
      }

      // Validate hospital_id format (should be a non-empty string)
      if (typeof bookingData.hospital_id !== 'string' || bookingData.hospital_id.trim() === '') {
        throw new Error('Invalid hospital ID format. Please contact support.');
      }

      // Final validation: Ensure hospital_id is present and valid before making the request
      if (!bookingData.hospital_id) {
        throw new Error('Hospital ID is required for booking. Please log out and log in again, then try booking.');
      }

      // Validate hospital_id format (should be a UUID format)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(bookingData.hospital_id)) {
        // Don't throw error, just validate - backend will validate
      }

      // COMPATIBILITY: Backend booking endpoint accepts `hospitalId` (camelCase) in request body
      // We already ensure `hospital_id` (snake_case) is correct, now mirror it to `hospitalId`
      bookingData.hospitalId = bookingData.hospital_id;

      // CRITICAL: Verify and use the hospital_id from localStorage (most reliable source)
      // This ensures we always use the hospital_id that was stored during login
      if (typeof window !== 'undefined') {
        try {
          // Priority 1: Get from dedicated key
          let verifiedHospitalId = localStorage.getItem(API_CONFIG.STORAGE_KEYS.HOSPITAL_ID);

          // Priority 2: Get from hospital_data
          if (!verifiedHospitalId) {
            const hospitalDataStr = localStorage.getItem(API_CONFIG.STORAGE_KEYS.HOSPITAL_DATA);
            if (hospitalDataStr) {
              const hospitalData = JSON.parse(hospitalDataStr);
              verifiedHospitalId = hospitalData.hospital_id || hospitalData.id;
            }
          }

          // Priority 3: Get from user_data
          if (!verifiedHospitalId) {
            const userDataStr = localStorage.getItem(API_CONFIG.STORAGE_KEYS.USER_DATA);
            if (userDataStr) {
              const userData = JSON.parse(userDataStr);
              verifiedHospitalId = userData.hospital_id || userData.hospitalId || userData.hospital?.hospital_id;
            }
          }

          // ALWAYS use the verified hospital_id from localStorage (overrides any passed value)
          if (verifiedHospitalId) {
            const verifiedId = String(verifiedHospitalId).trim();
            bookingData.hospital_id = verifiedId;
          } else {
            throw new Error('Hospital ID not found. Please log out and log in again.');
          }
        } catch (e) {
          throw new Error('Unable to verify hospital ID. Please log out and log in again.');
        }
      }

      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.CREATE_BOOKING;
      const response = await v1Api.post(endpoint, bookingData);
      return handleApiSuccess(response);
    } catch (error) {
      // Enhanced error handling for "Selected hospital is not available" error
      if (error.response?.data?.message?.includes('not available') ||
        error.response?.data?.message?.includes('Selected hospital')) {
        // Provide a more helpful error message
        const enhancedError = new Error(
          error.response?.data?.message ||
          'The selected hospital is not available. Please ensure you are logged in with the correct hospital account and try again. If the issue persists, please contact support.'
        );
        enhancedError.response = error.response;
        enhancedError.status = error.response?.status;
        throw enhancedError;
      }

      throw handleApiError(error);
    }
  }

  async getActiveBookings(params = {}) {
    try {
      const response = await v1Api.get(API_CONFIG.HOSPITAL_ENDPOINTS.GET_ACTIVE_BOOKINGS, { params });

      // Handle the response structure: { success: true, data: [...], pagination: {...} }
      const result = handleApiSuccess(response);

      // Ensure pagination is included in the result for usePagination hook
      if (response.data?.pagination) {
        result.pagination = response.data.pagination;
        // Convert totalPages to pages if needed
        if (result.pagination.totalPages && !result.pagination.pages) {
          result.pagination.pages = result.pagination.totalPages;
        }
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getBookingHistory(params = {}) {
    try {
      const response = await v1Api.get(API_CONFIG.HOSPITAL_ENDPOINTS.GET_BOOKING_HISTORY, { params });

      // Handle the nested response structure: { success: true, data: { data: [...], pagination: {...} } }
      // The API returns: { success: true, message: "...", data: { data: [...], pagination: {...} } }
      // After handleApiSuccess, result.data will be { data: [...], pagination: {...} }
      const result = handleApiSuccess(response);

      // Extract the nested data structure
      if (result.data && typeof result.data === 'object' && result.data.data) {
        // Nested structure: result.data.data is the bookings array
        const bookingsArray = result.data.data;
        const paginationInfo = result.data.pagination;

        // Replace result.data with the bookings array
        result.data = Array.isArray(bookingsArray) ? bookingsArray : [];

        // Add pagination to result
        if (paginationInfo) {
          result.pagination = paginationInfo;
          // Convert totalPages to pages if needed
          if (result.pagination.totalPages && !result.pagination.pages) {
            result.pagination.pages = result.pagination.totalPages;
          }
        }
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * GET /v1/hospital/vehicles/:vehicle_id/history
   * Returns vehicle info, summary (total_bookings, completed_bookings, active_bookings, cancelled_bookings), history array, pagination.
   */
  async getVehicleHistory(vehicleId, params = {}) {
    try {
      const basePath = API_CONFIG.HOSPITAL_ENDPOINTS.GET_VEHICLE_HISTORY;
      const url = `${basePath}/${encodeURIComponent(vehicleId)}/history`;
      const response = await v1Api.get(url, { params });
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async smartMatch(matchData) {
    try {
      const hospitalSmartMatchUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.SMART_MATCH);
      const response = await api.post(hospitalSmartMatchUrl, matchData);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getBookingDetails(bookingId) {
    try {
      // New endpoint: /v1/hospital/bookings/:bookingId
      // Response structure: { success: true, message: "...", data: { booking_id: "...", status: "...", booking_type: "...", patient_name: "...", pickup_address: "...", dropoff_address: "...", created_at: "..." } }
      const hospitalBookingDetailsUrl = `${getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.GET_BOOKING_DETAILS)}/${bookingId}`;
      const response = await api.get(hospitalBookingDetailsUrl);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Initiate payment for a hospital booking.
   * POST /v1/hospital/bookings/:bookingId/initiate-payment
   * @param {string} bookingId - Booking ID
   * @param {object} payload - { idempotencyKey: string, fare: string }
   * @returns {Promise<{ success: boolean, message: string, data: { order, keyId, isExisting } }>}
   */
  async initiateBookingPayment(bookingId, payload) {
    try {
      const url = `${getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.INITIATE_BOOKING_PAYMENT)}/${bookingId}/initiate-payment`;
      const response = await api.post(url, payload);
      return handleApiSuccess(response);
    } catch (error) {
      const errObj = handleApiError(error);
      throw new Error(errObj.message || 'Failed to initiate payment.');
    }
  }

  async getBookingRoute(bookingId) {
    try {
      // New endpoint: /v1/hospital/bookings/:booking_id/tracking/route
      // Response: { success: true, message: "...", data: [{ latitude, longitude, timestamp, speed, heading }, ...] }
      console.log('🗺️ [HospitalService] Fetching booking route for:', bookingId);
      const routeUrl = `${getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.GET_BOOKING_TRACKING)}/${bookingId}/tracking/route`;
      const response = await api.get(routeUrl);
      const result = handleApiSuccess(response);

      console.log('✅ [HospitalService] Route data received:', {
        success: result.success,
        dataLength: result.data?.length || 0,
        firstPoint: result.data?.[0],
        lastPoint: result.data?.[result.data?.length - 1]
      });

      return result;
    } catch (error) {
      console.error('❌ [HospitalService] Error fetching booking route:', error);
      return handleApiError(error);
    }
  }

  async getBookingTracking(bookingId) {
    try {
      // New endpoint: /v1/hospital/bookings/:bookingId/tracking
      // Response structure: { success: true, message: "...", data: { bookingId, status, hospital, vehicle, driver, pickupLocation, tracking: { driverLocation, ambulanceLocation, eta, distance, lastUpdated }, recentStatusHistory, ... } }
      const hospitalBookingTrackingUrl = `${getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.GET_BOOKING_TRACKING)}/${bookingId}/tracking`;
      const response = await api.get(hospitalBookingTrackingUrl);
      const result = handleApiSuccess(response);

      // Transform response to match expected structure for BookingTrackingCard
      // New response structure includes: bookingId, status, hospital, vehicle, driver, pickupLocation, tracking, schedule, recentStatusHistory
      // Expected: { bookingId, status, bookingType, scheduledDateTime, pickupLocation, dropoffLocation, createdAt, hospital, driver, ambulance, tracking: { eta, distance, driverLocation, lastUpdated } }

      // Check if booking exists - if result.data is null or result.success is false, booking not found
      if (!result.success || !result.data) {
        const error = new Error('Booking not found');
        error.status = 404;
        throw error;
      }

      if (result.data) {
        const trackingData = result.data;

        // Handle driverLocation from tracking.driverLocation (new structure)
        let driverLocation = null;
        if (trackingData.tracking?.driverLocation) {
          const dl = trackingData.tracking.driverLocation;
          driverLocation = {
            latitude: typeof dl.latitude === 'string' ? parseFloat(dl.latitude) : dl.latitude,
            longitude: typeof dl.longitude === 'string' ? parseFloat(dl.longitude) : dl.longitude,
            lastUpdated: dl.lastUpdated ? new Date(dl.lastUpdated) : new Date()
          };
        }
        // Fallback: Handle ambulanceLocation if driverLocation is not available
        else if (trackingData.tracking?.ambulanceLocation) {
          const al = trackingData.tracking.ambulanceLocation;
          driverLocation = {
            latitude: typeof al.latitude === 'string' ? parseFloat(al.latitude) : al.latitude,
            longitude: typeof al.longitude === 'string' ? parseFloat(al.longitude) : al.longitude,
            lastUpdated: al.lastUpdated ? new Date(al.lastUpdated) : new Date()
          };
        }

        // Handle ETA - convert ISO date string to Date object if present
        let eta = null;
        if (trackingData.tracking?.eta) {
          eta = new Date(trackingData.tracking.eta);
        }

        // Handle scheduled date/time from schedule object
        let scheduledDateTime = null;
        if (trackingData.schedule?.startTime) {
          scheduledDateTime = new Date(trackingData.schedule.startTime);
        }

        // Determine booking type from schedule or status
        let bookingType = null;
        if (trackingData.schedule && (trackingData.schedule.startTime || trackingData.schedule.endTime)) {
          bookingType = 'SCHEDULED';
        }

        // Transform to expected structure
        result.data = {
          bookingId: trackingData.bookingId || bookingId,
          status: trackingData.status || trackingData.statusId,
          statusId: trackingData.statusId,
          bookingType: bookingType || trackingData.bookingType,
          scheduledDateTime: scheduledDateTime || trackingData.scheduledDateTime || trackingData.scheduled_date_time || trackingData.scheduled_date,
          pickupLocation: trackingData.pickupLocation ? {
            address: trackingData.pickupLocation.address,
            latitude: trackingData.pickupLocation.latitude,
            longitude: trackingData.pickupLocation.longitude
          } : null,
          dropoffLocation: trackingData.dropoffLocation ? {
            address: trackingData.dropoffLocation.address,
            latitude: trackingData.dropoffLocation.latitude,
            longitude: trackingData.dropoffLocation.longitude
          } : null,
          createdAt: trackingData.created_at || trackingData.createdAt,
          hospital: trackingData.hospital,
          driver: trackingData.driver,
          ambulance: trackingData.vehicle || trackingData.ambulance,
          tracking: {
            eta: eta,
            distance: trackingData.tracking?.distance || null,
            driverLocation: driverLocation,
            lastUpdated: trackingData.tracking?.lastUpdated ? new Date(trackingData.tracking.lastUpdated) : new Date()
          },
          recentStatusHistory: trackingData.recentStatusHistory || [],
          journey_direction: trackingData.journey_direction || trackingData.journeyDirection || trackingData.booking_direction || trackingData.direction,
        };
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Estimate fare (Fare Estimate V2).
   * Uses POST /v1/hospital/bookings/estimate-fare-v2 when available; includes hospitalId for city resolution and contract.
   * Response should include per-estimate: total, breakdown, pricing_source, model.
   */
  async estimateFare(pickupLocation, dropoffLocation, options = {}) {
    try {
      const endpoint = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.ESTIMATE_FARE);

      const payload = {
        pickupLocation: {
          latitude: String(pickupLocation.latitude),
          longitude: String(pickupLocation.longitude)
        },
        dropoffLocation: {
          latitude: String(dropoffLocation.latitude),
          longitude: String(dropoffLocation.longitude)
        }
      };

      const hospitalId = options.hospitalId ?? (typeof window !== 'undefined' && (() => {
        try {
          const raw = localStorage.getItem('aps_hospital_id') || localStorage.getItem('aps_user_data');
          if (raw) {
            const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
            return data?.hospital_id ?? data?.hospitalId ?? data?.id;
          }
        } catch (_) { }
        return undefined;
      })());
      if (hospitalId != null && hospitalId !== '') payload.hospitalId = String(hospitalId);

      const response = await v1Api.post(endpoint, payload);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getCandidates(candidatesData) {
    try {
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.GET_CANDIDATES;
      console.log('📡 [HospitalService] Calling GET_CANDIDATES endpoint:', endpoint);

      // Prepare payload with required fields
      const payload = {};

      // If bookingId is provided, use it (for timeout refetch scenario)
      if (candidatesData.bookingId) {
        payload.bookingId = String(candidatesData.bookingId); // Ensure it's a string
        console.log('📡 [HospitalService] Adding bookingId to payload:', payload.bookingId);
      }

      // Note: When bookingId is provided, location fields are still required by the API

      // Location fields (required if no bookingId, but also include for bookingId scenario)
      if (candidatesData.pickupLocation) {
        payload.pickupLocation = {
          address: candidatesData.pickupLocation.address || "",
          latitude: typeof candidatesData.pickupLocation.latitude === 'number'
            ? candidatesData.pickupLocation.latitude
            : parseFloat(candidatesData.pickupLocation.latitude),
          longitude: typeof candidatesData.pickupLocation.longitude === 'number'
            ? candidatesData.pickupLocation.longitude
            : parseFloat(candidatesData.pickupLocation.longitude)
        };
      }

      if (candidatesData.dropoffLocation) {
        payload.dropoffLocation = {
          address: candidatesData.dropoffLocation.address || "",
          latitude: typeof candidatesData.dropoffLocation.latitude === 'number'
            ? candidatesData.dropoffLocation.latitude
            : parseFloat(candidatesData.dropoffLocation.latitude),
          longitude: typeof candidatesData.dropoffLocation.longitude === 'number'
            ? candidatesData.dropoffLocation.longitude
            : parseFloat(candidatesData.dropoffLocation.longitude)
        };
      }

      // Add optional fields if provided
      if (candidatesData.operator_id) {
        payload.operator_id = candidatesData.operator_id;
      }
      if (candidatesData.distance_range_km) {
        payload.distance_range_km = candidatesData.distance_range_km;
      }
      if (candidatesData.ambulance_type_ids && Array.isArray(candidatesData.ambulance_type_ids)) {
        payload.ambulance_type_ids = candidatesData.ambulance_type_ids;
      }
      if (candidatesData.payment_method) {
        payload.payment_method = candidatesData.payment_method;
      }

      console.log('📡 [HospitalService] Final payload:', JSON.stringify(payload, null, 2));
      console.log('📡 [HospitalService] Making POST request to:', endpoint);

      const response = await v1Api.post(endpoint, payload);
      console.log('📡 [HospitalService] API response received:', {
        status: response?.status,
        hasData: !!response?.data,
        responseKeys: response?.data ? Object.keys(response.data) : []
      });

      const result = handleApiSuccess(response);
      console.log('📡 [HospitalService] Processed response:', {
        success: result?.success,
        hasData: !!result?.data,
        hasCandidates: !!result?.data?.candidates
      });

      return result;
    } catch (error) {
      console.error('❌ [HospitalService] Error in getCandidates:', error);
      console.error('❌ [HospitalService] Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      throw handleApiError(error);
    }
  }

  async startManualAllocation(bookingPayload) {
    try {
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.START_MANUAL_ALLOCATION;
      const payload = {
        booking_payload: bookingPayload
      };
      console.log('🚑 [Manual Allocation] Starting manual allocation with payload:', payload);
      const response = await v1Api.post(endpoint, payload);
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [Manual Allocation] Error starting manual allocation:', error);
      throw handleApiError(error);
    }
  }

  async startAutoAllocation(bookingPayload) {
    try {
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.START_AUTO_ALLOCATION;
      const payload = {
        booking_payload: bookingPayload
      };
      console.log('🔄 [Auto Allocation] Starting auto allocation with payload:', payload);
      const response = await v1Api.post(endpoint, payload);
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [Auto Allocation] Error starting auto allocation:', error);
      throw handleApiError(error);
    }
  }

  async getAllocationStatus(bookingId) {
    try {
      const endpoint = `${API_CONFIG.HOSPITAL_ENDPOINTS.GET_ALLOCATION_STATUS}/${bookingId}/allocation-status`;
      const response = await v1Api.get(endpoint);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async stopAllocation(bookingId) {
    try {
      const endpoint = `${API_CONFIG.HOSPITAL_ENDPOINTS.STOP_ALLOCATION}/${bookingId}/stop-allocation`;
      console.log('🛑 [Stop Allocation] Stopping allocation for booking:', bookingId);
      const response = await v1Api.post(endpoint);
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [Stop Allocation] Error stopping allocation:', error);
      throw handleApiError(error);
    }
  }

  async triggerNextDriver(bookingId) {
    try {
      const endpoint = `${API_CONFIG.HOSPITAL_ENDPOINTS.TRIGGER_NEXT_DRIVER}/${bookingId}/next`;
      console.log('🔄 [Trigger Next Driver] Calling API:', endpoint);
      const response = await v1Api.post(endpoint);
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [Trigger Next Driver] Error:', error);
      throw handleApiError(error);
    }
  }

  async getMyBookings(params = {}) {
    try {
      // Use the new active bookings endpoint for hospital users
      return await this.getActiveBookings(params);
    } catch (error) {
      throw error;
    }
  }

  // Legacy method - use getBookingDetails instead
  // This method is kept for backward compatibility but routes to the new endpoint
  async getBookingById(bookingId) {
    return await this.getBookingDetails(bookingId);
  }

  async updateBookingStatus(bookingId, statusData) {
    try {
      // New endpoint: /v1/hospital/bookings/:bookingId/status
      // Response structure: { success: true, message: "...", data: { booking_id: "...", old_status: "...", new_status: "...", updated_at: "..." } }
      const hospitalBookingStatusUrl = `${getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.UPDATE_BOOKING_STATUS)}/${bookingId}/status`;
      const response = await api.put(hospitalBookingStatusUrl, statusData);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async cancelBooking(bookingId, cancelData) {
    try {
      // New endpoint: /v1/hospital/bookings/:bookingId/cancel
      // Response structure: { success: true, message: "...", data: { booking_id: "...", status: "CANCELLED", refund_amount: null, refund_status: null, cancelled_at: "..." } }
      const hospitalBookingCancelUrl = `${getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.CANCEL_BOOKING)}/${bookingId}/cancel`;
      const response = await api.put(hospitalBookingCancelUrl, cancelData || {});
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getBookingStatusHistory(bookingId) {
    try {
      // New endpoint: /v1/hospital/bookings/:bookingId/status-history
      // Response structure: { success: true, message: "...", data: [{ id: "...", old_status: null, new_status: "...", changed_by: "...", reason: null, created_at: "..." }] }
      const hospitalBookingStatusHistoryUrl = `${getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.GET_BOOKING_STATUS_HISTORY)}/${bookingId}/status-history`;
      const response = await api.get(hospitalBookingStatusHistoryUrl);
      const result = handleApiSuccess(response);

      // Transform response to match expected structure
      // New response: data is an array of status history items
      // Expected: array of { id, oldStatus, newStatus, changedBy, reason, changedAt }
      if (result.data && Array.isArray(result.data)) {
        result.data = result.data.map((item) => ({
          id: item.id,
          oldStatus: item.old_status,
          newStatus: item.new_status,
          changedBy: item.changed_by,
          reason: item.reason,
          changedAt: item.created_at
        }));
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getBookingTrackingEvents(bookingId) {
    try {
      // New endpoint: /v1/hospital/bookings/:bookingId/tracking/events
      // Response structure: { success: true, message: "...", data: [{ id, status, source, latitude, longitude, accuracy, notes, driver_name, timestamp }] }
      const trackingEventsUrl = `${getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.GET_BOOKING_TRACKING_EVENTS)}/${bookingId}/tracking/events`;
      const response = await api.get(trackingEventsUrl);
      const result = handleApiSuccess(response);

      // Return the tracking events array directly
      // Response: { success: true, message: "...", data: [...] }
      // After handleApiSuccess: result.data is the array of events
      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getBookingAnalytics(params = {}) {
    try {
      const response = await apiService.get('/hospital/bookings/analytics', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // HOSPITAL REPORTS
  // ============================================================================

  async generateReport(reportType, params = {}) {
    try {
      const response = await apiService.post('/hospital/reports/generate', {
        reportType,
        ...params
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getReportHistory(params = {}) {
    try {
      const response = await apiService.get('/hospital/reports/history', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async downloadReport(reportId) {
    try {
      const response = await apiService.get(`/api/hospital/reports/${reportId}/download`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // HOSPITAL SETTINGS
  // ============================================================================

  async getSettings() {
    try {
      const response = await apiService.get('/hospital/settings');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateSettings(settingsData) {
    try {
      const response = await apiService.put('/hospital/settings', settingsData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getEmergencyContacts() {
    try {
      const response = await apiService.get('/hospital/emergency-contacts');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateEmergencyContacts(contactsData) {
    try {
      const response = await apiService.put('/hospital/emergency-contacts', contactsData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get hospital by operator ID
   */
  async getHospitalByOperatorId(operatorId) {
    return await apiService.get(`/hospital/by-operator/${operatorId}`);
  }

  /**
   * Get all hospitals (for SUPER_ADMIN)
   */
  async getAllHospitals(params = {}) {
    return await apiService.get('/hospital/all', params);
  }

  // ============================================================================
  // HOSPITAL PARTNERSHIPS
  // ============================================================================

  async searchPartnerships(params = {}) {
    try {
      const partnershipsSearchUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.SEARCH_PARTNERSHIPS);
      const response = await api.get(partnershipsSearchUrl, { params });

      // Handle the nested response structure: { success: true, data: { data: [...], pagination: {...} } }
      const result = handleApiSuccess(response);

      // Extract the nested data structure
      if (result.data && typeof result.data === 'object' && result.data.data) {
        // Nested structure: result.data.data is the partnerships array
        const partnershipsArray = result.data.data;
        const paginationInfo = result.data.pagination;

        // Replace result.data with the partnerships array
        result.data = Array.isArray(partnershipsArray) ? partnershipsArray : [];

        // Add pagination to result
        if (paginationInfo) {
          result.pagination = paginationInfo;
          // Convert totalPages to pages if needed
          if (result.pagination.totalPages && !result.pagination.pages) {
            result.pagination.pages = result.pagination.totalPages;
          }
        }
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async updatePartnership(partnershipId, updateData) {
    try {
      // CRITICAL: Use UPDATE_PARTNERSHIP endpoint, NOT SEARCH_PARTNERSHIPS
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.UPDATE_PARTNERSHIP;

      // Use v1Api.put() with endpoint path (not full URL) - v1Api uses BASE_URL_V1
      // Endpoint format: /v1/hospital/partnerships/:partnershipId
      const endpointPath = `${endpoint}/${partnershipId}`;
      const response = await v1Api.put(endpointPath, updateData);

      const result = handleApiSuccess(response);

      // Handle the nested response structure: { success: true, message: "...", data: { data: {...} } }
      // The API returns: { success: true, message: "...", data: { data: { partnership object } } }
      if (result.data && typeof result.data === 'object') {
        if (result.data.data && typeof result.data.data === 'object') {
          // Nested structure: extract the inner data object
          result.data = result.data.data;
        }
        // If data is already the partnership object, use it as is
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async deletePartnership(partnershipId) {
    try {
      // Endpoint: DELETE /v1/hospital/partnerships/:partnershipId
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.UPDATE_PARTNERSHIP; // /v1/hospital/partnerships

      // Use v1Api.delete() with endpoint path (not full URL) - v1Api uses BASE_URL_V1
      // Endpoint format: /v1/hospital/partnerships/:partnershipId
      const endpointPath = `${endpoint}/${partnershipId}`;
      const response = await v1Api.delete(endpointPath);

      const result = handleApiSuccess(response);

      // Handle the nested response structure: { success: true, message: "...", data: { data: {...} } }
      // The API returns: { success: true, message: "Partnership terminated successfully", data: { data: { partnership object } } }
      if (result.data && typeof result.data === 'object') {
        if (result.data.data && typeof result.data.data === 'object') {
          // Nested structure: extract the inner data object
          result.data = result.data.data;
        }
        // If data is already the partnership object, use it as is
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getPartnershipPerformance(partnershipId) {
    try {
      // Endpoint: GET /v1/hospital/partnerships/:partnershipId/performance
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.GET_PARTNERSHIP_PERFORMANCE;

      // Use v1Api.get() with endpoint path (not full URL) - v1Api uses BASE_URL_V1
      // Endpoint format: /v1/hospital/partnerships/:partnershipId/performance
      const endpointPath = `${endpoint}/${partnershipId}/performance`;
      const response = await v1Api.get(endpointPath);

      const result = handleApiSuccess(response);

      // Handle the nested response structure: { success: true, message: "...", data: { data: {...} } }
      // The API returns: { success: true, message: "Partnership performance retrieved successfully", data: { data: { performance object } } }
      if (result.data && typeof result.data === 'object') {
        if (result.data.data && typeof result.data.data === 'object') {
          // Nested structure: extract the inner data object
          result.data = result.data.data;
        }
        // If data is already the performance object, use it as is
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getPartnershipRequests(params = {}) {
    try {
      // Endpoint: GET /v1/hospital/partnerships/invitations?status=PENDING
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.GET_PARTNERSHIP_REQUESTS;

      // Use v1Api.get() with endpoint path and query params
      const response = await v1Api.get(endpoint, { params });

      const result = handleApiSuccess(response);

      // Handle the nested response structure: { success: true, message: "...", data: { data: [...], pagination: {...} } }
      // The API returns: { success: true, message: "Partnership invitations retrieved successfully", data: { data: [...], pagination: {...} } }
      if (result.data && typeof result.data === 'object' && result.data.data) {
        // Nested structure: result.data.data is the invitations array
        const invitationsArray = result.data.data;
        const paginationInfo = result.data.pagination;

        // Replace result.data with the invitations array
        result.data = Array.isArray(invitationsArray) ? invitationsArray : [];

        // Add pagination to result
        if (paginationInfo) {
          result.pagination = paginationInfo;
          // Convert totalPages to pages if needed
          if (result.pagination.totalPages && !result.pagination.pages) {
            result.pagination.pages = result.pagination.totalPages;
          }
        }
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Partnership invitations sent TO this hospital (incoming from fleets)
   * GET /v1/hospital/partnerships/invitations
   */
  async getPartnershipInvitations(params = {}) {
    try {
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.RESPOND_TO_PARTNERSHIP_INVITATION;
      const response = await v1Api.get(endpoint, { params });
      const result = handleApiSuccess(response);

      // Expected shape:
      // { success, message, data: { data: [...], pagination: {...} } }
      if (result.data && typeof result.data === 'object' && result.data.data) {
        const invitationsArray = result.data.data;
        const paginationInfo = result.data.pagination;

        result.data = Array.isArray(invitationsArray) ? invitationsArray : [];

        if (paginationInfo) {
          result.pagination = paginationInfo;
          if (result.pagination.totalPages && !result.pagination.pages) {
            result.pagination.pages = result.pagination.totalPages;
          }
        }
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async updatePartnershipRequest(requestId, updateData) {
    try {
      // Endpoint: PUT /v1/hospital/partnerships/requests/:requestId
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.UPDATE_PARTNERSHIP_REQUEST;

      // Use v1Api.put() with endpoint path and request ID
      const endpointPath = `${endpoint}/${requestId}`;
      const response = await v1Api.put(endpointPath, updateData);

      const result = handleApiSuccess(response);

      // The API returns: { success: true, message: "...", data: { request object } }
      // Return the updated request data
      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async deletePartnershipRequest(requestId) {
    try {
      // Endpoint: DELETE /v1/hospital/partnerships/requests/:requestId
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.DELETE_PARTNERSHIP_REQUEST;

      // Use v1Api.delete() with endpoint path and request ID
      const endpointPath = `${endpoint}/${requestId}`;
      const response = await v1Api.delete(endpointPath);

      const result = handleApiSuccess(response);

      // The API returns: { success: true, message: "Partnership request cancelled successfully", data: { request object with status CANCELLED } }
      // Return the cancelled request data
      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async respondToPartnershipInvitation(requestId, status) {
    try {
      // Endpoint: PUT /v1/hospital/partnerships/invitations/:requestId/respond
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.RESPOND_TO_PARTNERSHIP_INVITATION;

      // Use v1Api.put() with endpoint path and request body
      const endpointPath = `${endpoint}/${requestId}/respond`;
      const response = await v1Api.put(endpointPath, { status });

      const result = handleApiSuccess(response);

      // Handle the nested response structure: { success: true, message: "...", data: { data: {...} } }
      // The API returns: { success: true, message: "...", data: { data: { request object, partnership_id } } }
      if (result.data && typeof result.data === 'object' && result.data.data) {
        // Extract the inner data object
        result.data = result.data.data;
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // ============================================================================
  // HOSPITAL AVAILABLE FLEETS
  // ============================================================================

  async getAvailableFleets(params = {}) {
    try {
      // Get hospital ID from localStorage
      let hospitalId = null;
      if (typeof window !== 'undefined') {
        try {
          // Priority 1: Get from dedicated key
          const hospitalIdKey = API_CONFIG.STORAGE_KEYS.HOSPITAL_ID;
          hospitalId = localStorage.getItem(hospitalIdKey);

          // Priority 2: Get from hospital_data
          if (!hospitalId) {
            const hospitalDataKey = API_CONFIG.STORAGE_KEYS.HOSPITAL_DATA;
            const hospitalDataStr = localStorage.getItem(hospitalDataKey);
            if (hospitalDataStr) {
              const hospitalData = JSON.parse(hospitalDataStr);
              hospitalId = hospitalData.hospital_id || hospitalData.id;
            }
          }

          // Priority 3: Get from user_data
          if (!hospitalId) {
            const userDataKey = API_CONFIG.STORAGE_KEYS.USER_DATA;
            const userDataStr = localStorage.getItem(userDataKey);
            if (userDataStr) {
              const userData = JSON.parse(userDataStr);
              hospitalId = userData.hospital_id || userData.hospitalId || userData.hospital?.hospital_id;
            }
          }
        } catch (error) {
          console.error('Error getting hospital ID from localStorage:', error);
        }
      }

      if (!hospitalId) {
        throw new Error('Hospital ID not found. Please ensure you are logged in as a hospital user.');
      }

      // Endpoint: GET /v1/hospital/:hospitalId/available-fleets
      const endpointPath = `/v1/hospital/${hospitalId}/available-fleets`;
      const response = await v1Api.get(endpointPath, { params });

      const result = handleApiSuccess(response);

      // Handle the response structure: { success: true, message: "...", data: [...], pagination: {...} }
      // The API returns: { success: true, message: "...", data: [...], pagination: {...} }
      // Extract pagination if it exists
      if (response.data?.pagination) {
        result.pagination = response.data.pagination;
        // Convert totalPages to pages if needed
        if (result.pagination.totalPages && !result.pagination.pages) {
          result.pagination.pages = result.pagination.totalPages;
        }
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async inviteFleet(fleetOperatorIdOrDetails, expiresAt = null, requestMessage = null) {
    try {
      // If expiresAt is not provided, set it to end of year 2025 (as per example)
      if (!expiresAt) {
        expiresAt = '2025-12-31T23:59:59Z';
      }

      // Endpoint: POST /v1/hospital/partnerships/invite-fleet
      const endpointPath = '/v1/hospital/partnerships/invite-fleet';

      let requestBody;

      // Check if fleetOperatorIdOrDetails is a string (existing fleet ID) or an object (new fleet details)
      if (typeof fleetOperatorIdOrDetails === 'string') {
        // Existing fleet invitation - use fleet_operator_id
        requestBody = {
          fleet_operator_id: fleetOperatorIdOrDetails,
          expires_at: expiresAt
        };
      } else if (typeof fleetOperatorIdOrDetails === 'object' && fleetOperatorIdOrDetails !== null) {
        // New fleet invitation - use fleet details
        requestBody = {
          fleet_name: fleetOperatorIdOrDetails.name || fleetOperatorIdOrDetails.fleet_name,
          fleet_email: fleetOperatorIdOrDetails.email || fleetOperatorIdOrDetails.fleet_email,
          fleet_phone: fleetOperatorIdOrDetails.contact_info || fleetOperatorIdOrDetails.phone || fleetOperatorIdOrDetails.fleet_phone,
          fleet_address: fleetOperatorIdOrDetails.address || fleetOperatorIdOrDetails.fleet_address,
          fleet_city: fleetOperatorIdOrDetails.city || fleetOperatorIdOrDetails.fleet_city,
          fleet_state: fleetOperatorIdOrDetails.state || fleetOperatorIdOrDetails.fleet_state,
          expires_at: expiresAt
        };

        // Add request_message if provided
        if (requestMessage || fleetOperatorIdOrDetails.request_message) {
          requestBody.request_message = requestMessage || fleetOperatorIdOrDetails.request_message;
        }
      } else {
        throw new Error('Invalid fleet operator ID or details provided');
      }

      const response = await v1Api.post(endpointPath, requestBody);

      const result = handleApiSuccess(response);

      // Handle the nested response structure: { success: true, message: "...", data: { data: {...}, isNewFleet: false/true, message: "..." } }
      // The API returns: { success: true, message: "...", data: { data: { request object }, isNewFleet: false/true, message: "..." } }
      if (result.data && typeof result.data === 'object') {
        if (result.data.data && typeof result.data.data === 'object') {
          // Extract the inner data object (the request object)
          result.requestData = result.data.data;
        }
        // Keep the isNewFleet flag if it exists
        if (result.data.isNewFleet !== undefined) {
          result.isNewFleet = result.data.isNewFleet;
        }
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // ============================================================================
  // HOSPITAL PARTNER FLEET VEHICLES
  // ============================================================================

  async getPartnerFleetVehicles(params = {}) {
    try {
      // Endpoint: GET /v1/hospital/partner-fleet-vehicle
      const endpoint = API_CONFIG.HOSPITAL_ENDPOINTS.GET_PARTNER_FLEET_VEHICLES;

      // Use v1Api.get() with endpoint path and query params
      const response = await v1Api.get(endpoint, { params });

      const result = handleApiSuccess(response);

      // Handle the response structure: { success: true, message: "...", data: [...], pagination: {...} }
      // The API returns: { success: true, message: "Partner fleet vehicles retrieved successfully", data: [...], pagination: {...} }
      // Extract pagination if it exists
      if (response.data?.pagination) {
        result.pagination = response.data.pagination;
        // Convert totalPages to pages if needed
        if (result.pagination.totalPages && !result.pagination.pages) {
          result.pagination.pages = result.pagination.totalPages;
        }
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

// Create and export singleton instance
const hospitalService = new HospitalService();
export default hospitalService;
